/**
 * Node Definitions Utility
 * This file provides a bridge between the shared node definitions and the frontend
 * It maps the shared node definitions to the format expected by the UI components
 */

import {
  Zap,
  Code,
  Globe,
  GitBranch,
  Shuffle,
  Bot,
  Database,
  Mail,
  MessageSquare,
  FileText,
  Calendar,
  Clock,
  Filter,
  Repeat,
  AlertCircle,
  AlertTriangle,
  Webhook,
  ArrowUpDown,
  GitMerge,
  Play,
  DollarSign,
  Users,
  Receipt,
  CreditCard,
  BookOpen,
  PieChart,
  BarChart3,
  Settings,
  Scissors,
  Brain,
  Sparkles,
  FileCode,
  FileSearch,
  Layers,
  FileEdit,
  Timer,
  Hash,
  Braces,
  FileJson,
  FileX,
  Minimize2,
  Copy,
  SortAsc,
  Plug,
  Server,
  Palette,
  Workflow,
  Rss,
  Network,
  CircleSlash,
  GitCompare,
  Lock,
  MinusCircle,
  Search,
  Upload,
  Heart,
  Banknote,
  TableProperties,
  Table2,
  Columns,
  SplitSquareHorizontal,
  FunctionSquare,
  LayoutTemplate,
  StopCircle,
  Type,
  type LucideIcon,
} from 'lucide-react';

import { ALL_NODE_DEFINITIONS } from '@shared/nodes';
import type { NodeDefinition as SharedNodeDefinition } from '@shared/types/node-definition';
import { resolveIcon } from './iconResolver';
import { hasSimpleIcon } from './simpleIconsMap';

export interface NodeDefinition {
  type: string;
  label: string;
  name?: string; // Display name (alias for label)
  icon: LucideIcon | React.ComponentType<any>;
  iconType?: 'lucide' | 'simple-icon' | 'microsoft-icon' | 'integration-icon';
  iconColor?: string; // Brand color from Simple Icons
  color: string;
  bgColor: string;
  borderColor: string;
  subtitle: string;
  description: string;
  category: 'triggers' | 'ai-nodes' | 'access-integrations' | 'flow-control' | 'utilities' | 'database' | 'integrations' | 'web' | 'tools' | 'responses' | 'files' | 'anchor-tools' | 'anchor-chatModels' | 'anchor-memory' | 'anchor-parser' | 'anchor-pollSource';
  categories?: ('triggers' | 'ai-nodes' | 'access-integrations' | 'flow-control' | 'utilities' | 'database' | 'integrations' | 'web' | 'tools' | 'responses' | 'files' | 'anchor-tools' | 'anchor-chatModels' | 'anchor-memory' | 'anchor-parser' | 'anchor-pollSource')[]; // Multiple categories support
  tags: string[];
  comingSoon?: boolean;
  isSubNode?: boolean;
  subNodeConfig?: {
    category: string;
    parentNodeType?: string;
    anchorId?: string;
  };
  toolCapable?: {
    enabled: boolean;
    [key: string]: any;
  };
}

// Icon mapping from icon names to Lucide components
const iconMap: Record<string, LucideIcon> = {
  // Triggers
  'Zap': Zap,
  'Calendar': Calendar,
  'Webhook': Webhook,
  'AlertCircle': AlertCircle,
  'AlertTriangle': AlertTriangle,
  'Play': Play,
  'Workflow': Workflow,
  
  // Actions
  'Globe': Globe,
  'Code': Code,
  'Mail': Mail,
  'Bot': Bot,
  'Repeat': Repeat,
  'GitMerge': GitMerge,
  'Settings': Settings,
  'Network': Network,
  
  // Data
  'Shuffle': Shuffle,
  'Filter': Filter,
  'ArrowUpDown': ArrowUpDown,
  'FileText': FileText,
  'FileEdit': FileEdit,
  'LayoutTemplate': LayoutTemplate,
  'Layers': Layers,
  'Hash': Hash,
  'Braces': Braces,
  'FileJson': FileJson,
  'FileX': FileX,
  'Minimize2': Minimize2,
  'Copy': Copy,
  'SortAsc': SortAsc,
  
  // Logic
  'GitBranch': GitBranch,
  
  // AI
  'Brain': Brain,
  'Sparkles': Sparkles,
  'MessageSquare': MessageSquare,
  'Search': Search,
  'Upload': Upload,
  
  // Utilities
  'Clock': Clock,
  'Timer': Timer,
  'FileCode': FileCode,
  'FileSearch': FileSearch,
  'Scissors': Scissors,
  'CircleSlash': CircleSlash,
  'GitCompare': GitCompare,
  'Lock': Lock,
  'MinusCircle': MinusCircle,
  'StopCircle': StopCircle,
  'TableProperties': TableProperties,
  'SplitSquareHorizontal': SplitSquareHorizontal,
  'FunctionSquare': FunctionSquare,
  'Type': Type,

  // Database
  'Database': Database,
  'Table2': Table2,
  'Columns': Columns,
  
  // Tools
  'Plug': Plug,
  'Server': Server,
  'Palette': Palette,
  
  // Integration
  'DollarSign': DollarSign,
  'Users': Users,
  'Receipt': Receipt,
  'CreditCard': CreditCard,
  'Heart': Heart,
  'BookOpen': BookOpen,
  'PieChart': PieChart,
  'BarChart3': BarChart3,
  'Rss': Rss,
  'Banknote': Banknote,
};

// Color mapping from color names to hex values
const colorMap: Record<string, { color: string; bgColor: string; borderColor: string }> = {
  'orange': { color: '#f97316', bgColor: '#fff7ed', borderColor: '#fed7aa' },
  'amber': { color: '#f59e0b', bgColor: '#fffbeb', borderColor: '#fde68a' },
  'blue': { color: '#3b82f6', bgColor: '#eff6ff', borderColor: '#bfdbfe' },
  'yellow': { color: '#eab308', bgColor: '#fefce8', borderColor: '#fde047' },
  'green': { color: '#10b981', bgColor: '#f0fdf4', borderColor: '#bbf7d0' },
  'purple': { color: '#a855f7', bgColor: '#faf5ff', borderColor: '#e9d5ff' },
  'violet': { color: '#8b5cf6', bgColor: '#f5f3ff', borderColor: '#ddd6fe' },
  'red': { color: '#dc2626', bgColor: '#fef2f2', borderColor: '#fecaca' },
  'rose': { color: '#f43f5e', bgColor: '#fff1f2', borderColor: '#fecdd3' },
  'pink': { color: '#ec4899', bgColor: '#fdf2f8', borderColor: '#fbcfe8' },
  'indigo': { color: '#6366f1', bgColor: '#eef2ff', borderColor: '#c7d2fe' },
  'cyan': { color: '#06b6d4', bgColor: '#ecfeff', borderColor: '#a5f3fc' },
  'gray': { color: '#6b7280', bgColor: '#f9fafb', borderColor: '#d1d5db' },
  'emerald': { color: '#059669', bgColor: '#ecfdf5', borderColor: '#a7f3d0' },
  'teal': { color: '#14b8a6', bgColor: '#f0fdfa', borderColor: '#99f6e4' },
};

// Category mapping - maps shared category names to frontend category names
const categoryMap: Record<string, 'triggers' | 'ai-nodes' | 'access-integrations' | 'flow-control' | 'utilities' | 'database' | 'integrations' | 'web' | 'tools' | 'responses' | 'files' | 'anchor-tools' | 'anchor-chatModels' | 'anchor-memory' | 'anchor-pollSource'> = {
  'triggers': 'triggers',
  'ai-nodes': 'ai-nodes',
  'access-integrations': 'access-integrations',
  'flow-control': 'flow-control',
  'utilities': 'utilities',
  'database': 'database',
  'integrations': 'integrations',
  'web': 'web',
  'tools': 'tools',
  'responses': 'responses',
  'files': 'files',
  'anchor-tools': 'anchor-tools',
  'anchor-chatModels': 'anchor-chatModels',
  'anchor-memory': 'anchor-memory',
  'anchor-pollSource': 'anchor-pollSource',
  // Legacy mappings for backwards compatibility
  'actions': 'utilities',
  'ai': 'ai-nodes',
  'data': 'utilities',
  'access': 'access-integrations',
  'langchain': 'ai-nodes',
};

/**
 * Convert shared node definition to UI node definition
 */
function convertSharedDefinition(shared: SharedNodeDefinition): NodeDefinition {
  // Determine icon type - check if it's explicitly set or if we have a Simple Icons mapping
  const iconType = shared.iconType || (hasSimpleIcon(shared.type) ? 'simple-icon' : 'lucide');
  
  // Resolve the icon
  const resolvedIcon = resolveIcon(shared.icon, iconType, iconMap);
  
  const colors = colorMap[shared.color] || colorMap['gray'];
  
  // Handle multiple categories
  let primaryCategory: any;
  let allCategories: any[] | undefined;
  
  if (Array.isArray(shared.category)) {
    // Multiple categories - first one is primary
    allCategories = shared.category.map(cat => categoryMap[cat] || 'utilities');
    primaryCategory = allCategories[0];
  } else {
    // Single category
    primaryCategory = categoryMap[shared.category] || 'utilities';
    allCategories = [primaryCategory];
  }
  
  // Automatically add nodes with toolCapable to anchor-tools category
  if (shared.toolCapable?.enabled && !allCategories.includes('anchor-tools')) {
    allCategories = [...allCategories, 'anchor-tools'];
  }
  
  // Automatically add specific chat model nodes to anchor-chatModels category
  // OpenAI, Azure OpenAI, Anthropic (Claude), and Access LLM
  if ((shared.type === 'openai' || shared.type === 'azure-openai' || shared.type === 'anthropic' || shared.type === 'access-llm') && !allCategories.includes('anchor-chatModels')) {
    allCategories = [...allCategories, 'anchor-chatModels'];
  }
  
  // Automatically add specific memory nodes to anchor-memory category
  // Only Simple Memory (for now)
  if (shared.type === 'simpleMemory' && !allCategories.includes('anchor-memory')) {
    allCategories = [...allCategories, 'anchor-memory'];
  }
  
  // Automatically add parser nodes to anchor-parser category
  if (shared.isSubNode && shared.subNodeConfig?.category === 'parser' && !allCategories.includes('anchor-parser')) {
    allCategories = [...allCategories, 'anchor-parser'];
  }

  // Automatically add nodes that can return arrays to anchor-pollSource category
  // These are nodes that can be used as data sources for polling triggers
  const pollableCategories = ['access-integrations', 'database', 'integrations'];
  const pollableNodeTypes = ['http-request', 'httpRequest'];
  if (
    (pollableCategories.some(cat => allCategories.includes(cat)) || pollableNodeTypes.includes(shared.type)) &&
    !allCategories.includes('anchor-pollSource') &&
    !shared.isSubNode // Don't include sub-nodes as poll sources
  ) {
    allCategories = [...allCategories, 'anchor-pollSource'];
  }

  return {
    type: shared.type,
    label: shared.name,
    name: shared.name,
    icon: resolvedIcon.component,
    iconType: resolvedIcon.type,
    iconColor: resolvedIcon.color,
    color: colors.color,
    bgColor: colors.bgColor,
    borderColor: colors.borderColor,
    subtitle: shared.description.split(' ').slice(0, 3).join(' '), // First 3 words
    description: shared.description,
    category: primaryCategory,
    categories: allCategories.length > 1 ? allCategories : undefined,
    tags: shared.tags || [],
    comingSoon: shared.comingSoon,
    isSubNode: shared.isSubNode,
    subNodeConfig: shared.subNodeConfig,
    toolCapable: shared.toolCapable,
  };
}

// Build node definitions from shared definitions
export const nodeDefinitions: Record<string, NodeDefinition> = Object.entries(ALL_NODE_DEFINITIONS).reduce(
  (acc, [type, sharedDef]) => {
    acc[type] = convertSharedDefinition(sharedDef);
    return acc;
  },
  {} as Record<string, NodeDefinition>
);

export const getNodeDefinition = (type: string): NodeDefinition => {
  return (
    nodeDefinitions[type] || {
      type,
      label: type,
      icon: AlertCircle,
      color: '#6b7280',
      bgColor: '#f9fafb',
      borderColor: '#d1d5db',
      subtitle: 'Unknown',
      description: 'Unknown node type',
      category: 'utilities',
      tags: [],
    }
  );
};

export const getNodeIcon = (type: string): LucideIcon | React.ComponentType<any> => {
  return getNodeDefinition(type).icon;
};

export const getNodeColor = (type: string): string => {
  return getNodeDefinition(type).color;
};

export const getNodeSubtitle = (type: string, data?: any): string => {
  const definition = getNodeDefinition(type);
  
  // Custom subtitle logic based on node data
  if (type === 'http-request' && data?.method) {
    return `${data.method} request`;
  }
  if (type === 'code' && data?.language) {
    return `Run ${data.language}`;
  }
  if (type === 'ai-agent' && data?.model) {
    return data.model.split('-')[0].toUpperCase();
  }
  
  return definition.subtitle;
};

export const getAllNodeDefinitions = (): NodeDefinition[] => {
  return Object.values(nodeDefinitions);
};

export const getNodesByCategory = (category: string): NodeDefinition[] => {
  if (category === 'all') {
    return getAllNodeDefinitions();
  }
  return getAllNodeDefinitions().filter((node) => {
    // Check primary category
    if (node.category === category) return true;
    // Check additional categories
    if (node.categories && node.categories.includes(category as any)) return true;
    return false;
  });
};

export const searchNodes = (query: string): NodeDefinition[] => {
  const lowerQuery = query.toLowerCase();
  return getAllNodeDefinitions().filter(
    (node) =>
      node.label.toLowerCase().includes(lowerQuery) ||
      node.description.toLowerCase().includes(lowerQuery) ||
      node.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
};
